import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const COLORS = ['#2563eb','#16a34a','#d97706','#dc2626','#7c3aed','#0891b2'];

const StatCard = ({ icon, label, value, sub, color='#2563eb' }) => (
  <div style={s.statCard}>
    <div style={{ ...s.statIcon, background: color+'15', border:`1px solid ${color}25` }}>{icon}</div>
    <div style={{ ...s.statValue, color }}>{value}</div>
    <div style={s.statLabel}>{label}</div>
    {sub && <div style={s.statSub}>{sub}</div>}
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [users, setUsers] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchAll = async () => {
    setLoading(true); setError('');
    try {
      const [b, u, bg] = await Promise.all([
        api.get('/billing/cost-overview'),
        api.get('/users').catch(() => ({ data: { users: [] } })),
        api.get('/billing/budgets').catch(() => ({ data: { budgets: [] } })),
      ]);
      setBilling(b.data); setUsers(u.data.users || []); setBudgets(bg.data.budgets || []);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load billing data');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const projects = billing?.projects || [];
  const enabled = projects.filter(p => p.billingEnabled);
  const clients = users.filter(u => u.role === 'client');

  const barData = projects.map((p, i) => ({
    name: (p.projectName || p.projectId || '').substring(0, 18),
    billing: p.billingEnabled ? 1 : 0,
    fill: p.billingEnabled ? '#16a34a' : '#dc2626',
  }));

  const pieData = enabled.map((p, i) => ({ name: p.projectName || p.projectId, value: 1, color: COLORS[i % COLORS.length] }));

  return (
    <div style={s.layout}>
      <Sidebar />
      <main style={s.main}>

        {/* Header */}
        <div style={s.header}>
          <div>
            <div style={s.breadcrumb}>Dashboard</div>
            <h1 style={s.pageTitle}>Admin Overview</h1>
            <p style={s.pageSubtitle}>{new Date().toLocaleDateString('en-US', { weekday:'long', year:'numeric', month:'long', day:'numeric' })}</p>
          </div>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            {lastRefresh && <span style={s.refreshTime}>Updated {lastRefresh.toLocaleTimeString()}</span>}
            <button style={s.outlineBtn} onClick={fetchAll} disabled={loading}>{loading ? '⟳ Loading...' : '⟳ Refresh'}</button>
            <button style={s.primaryBtn} onClick={() => navigate('/admin/budgets')}>+ Set Budget</button>
          </div>
        </div>

        {error && <div style={s.errorBox}>⚠ {error}</div>}

        {loading && !billing && (
          <div style={s.skeletonGrid}>{[1,2,3,4].map(i => <div key={i} style={s.skeleton} />)}</div>
        )}

        {billing && (
          <>
            {/* Account info bar */}
            <div style={s.accountBar}>
              <div style={s.gcpBadge}>
                <svg width="16" height="16" viewBox="0 0 32 32"><path d="M16 2L28 9V23L16 30L4 23V9L16 2Z" fill="#4285F4"/><path d="M16 8L22 12V20L16 24L10 20V12L16 8Z" fill="#fff"/><circle cx="16" cy="16" r="3" fill="#4285F4"/></svg>
                Google Cloud Billing
              </div>
              {[
                { label:'Account', value: billing.billingAccount?.displayName || billing.billingAccount?.id },
                { label:'ID', value: billing.billingAccount?.id, mono: true },
                { label:'Status', value: billing.billingAccount?.open ? '● Active' : '○ Closed', color: billing.billingAccount?.open ? '#16a34a' : '#dc2626' },
                { label:'Currency', value: billing.billingAccount?.currencyCode || 'USD' },
              ].map((item, i) => (
                <div key={i} style={s.accountItem}>
                  <span style={s.accountLabel}>{item.label}</span>
                  <span style={{ ...s.accountValue, fontFamily: item.mono ? 'monospace' : 'inherit', fontSize: item.mono ? 11 : 13, color: item.color || '#0f172a' }}>{item.value}</span>
                </div>
              ))}
            </div>

            {/* Stat cards */}
            <div style={s.statsGrid}>
              <StatCard icon="📂" label="Total Projects" value={billing.totalProjects || 0} sub="Linked to billing account" color="#2563eb" />
              <StatCard icon="✅" label="Billing Enabled" value={billing.billingEnabledCount || 0} sub="Actively billed" color="#16a34a" />
              <StatCard icon="👥" label="Client Users" value={clients.length} sub={`${users.length} total accounts`} color="#d97706" />
              <StatCard icon="🎯" label="Budget Alerts" value={budgets.length} sub={budgets.length ? 'Configured' : 'None set'} color="#7c3aed" />
            </div>

            {/* Charts */}
            <div style={s.chartsRow}>
              <div style={s.chartCard}>
                <div style={s.chartHeader}>
                  <h3 style={s.chartTitle}>Projects — Billing Status</h3>
                  <span style={s.badge}>{projects.length} projects</span>
                </div>
                {projects.length === 0
                  ? <div style={s.empty}>No projects found on this billing account</div>
                  : <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={barData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="name" stroke="#94a3b8" tick={{ fill:'#64748b', fontSize:11 }} />
                        <YAxis stroke="#94a3b8" tick={{ fill:'#64748b', fontSize:11 }} ticks={[0,1]} tickFormatter={v => v===1?'On':'Off'} />
                        <Tooltip contentStyle={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, color:'#0f172a', boxShadow:'0 4px 12px rgba(0,0,0,0.1)' }} formatter={(v,n,p) => [p.payload.billing ? 'Billing Enabled' : 'Billing Disabled', '']} />
                        <Bar dataKey="billing" radius={[6,6,0,0]}>
                          {barData.map((e,i) => <Cell key={i} fill={e.fill} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                }
              </div>

              <div style={s.chartCard}>
                <div style={s.chartHeader}>
                  <h3 style={s.chartTitle}>Billing-Enabled Projects</h3>
                  <span style={s.badge}>Live from GCP</span>
                </div>
                {pieData.length === 0
                  ? <div style={s.empty}>No billing-enabled projects</div>
                  : <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} strokeWidth={2} stroke="#fff">
                          {pieData.map((e,i) => <Cell key={i} fill={e.color} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, boxShadow:'0 4px 12px rgba(0,0,0,0.1)' }} formatter={(v,name) => [name, '']} />
                        <Legend formatter={v => <span style={{ color:'#64748b', fontSize:12 }}>{v}</span>} />
                      </PieChart>
                    </ResponsiveContainer>
                }
              </div>
            </div>

            {/* Projects table */}
            <div style={s.tableCard}>
              <div style={s.tableHeader}>
                <h3 style={s.chartTitle}>All Projects</h3>
                <button style={s.linkBtn} onClick={() => navigate('/admin/costs')}>View full details →</button>
              </div>
              <table style={s.table}>
                <thead>
                  <tr style={{ background:'#f8fafc' }}>
                    {['Project Name','Project ID','Billing Status','Lifecycle'].map(h => <th key={h} style={s.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {projects.map((p, i) => (
                    <tr key={i} onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'} onMouseLeave={e=>e.currentTarget.style.background='#fff'} style={{ background:'#fff' }}>
                      <td style={s.td}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:8, height:8, borderRadius:2, background: COLORS[i%COLORS.length] }} />
                          <span style={{ color:'#0f172a', fontWeight:500 }}>{p.projectName || p.projectId}</span>
                        </div>
                      </td>
                      <td style={s.td}><code style={s.code}>{p.projectId}</code></td>
                      <td style={s.td}>
                        <span style={{ display:'inline-block', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:600, background: p.billingEnabled?'#dcfce7':'#fee2e2', color: p.billingEnabled?'#166534':'#991b1b' }}>
                          {p.billingEnabled ? '● Billing On' : '○ Billing Off'}
                        </span>
                      </td>
                      <td style={s.td}><span style={{ color: p.lifecycleState==='ACTIVE'?'#16a34a':'#94a3b8', fontSize:13 }}>{p.lifecycleState||'—'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Budgets summary */}
            {budgets.length > 0 && (
              <div style={s.tableCard}>
                <div style={s.tableHeader}>
                  <h3 style={s.chartTitle}>Active Budget Alerts</h3>
                  <button style={s.linkBtn} onClick={() => navigate('/admin/budgets')}>Manage →</button>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {budgets.map((b, i) => (
                    <div key={i} style={s.budgetRow}>
                      <code style={s.code}>{b.project_id}</code>
                      <span style={{ color:'#0f172a', fontWeight:600 }}>${Number(b.budget_amount).toLocaleString()}/mo</span>
                      <span style={{ background:'#fef9c3', color:'#854d0e', padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:600 }}>⚡ Alert at {b.alert_threshold_percent}%</span>
                      {b.email_notify && <span style={{ color:'#64748b', fontSize:12 }}>📧 {b.email_notify}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div style={s.statusBar}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:7, height:7, borderRadius:'50%', background:'#16a34a' }} />
                <span style={{ color:'#64748b', fontSize:13 }}>Live GCP Billing data · Account <strong style={{ color:'#1d4ed8' }}>{billing.billingAccount?.id}</strong></span>
              </div>
              <span style={{ color:'#94a3b8', fontSize:12 }}>{billing.fetchedAt ? `Fetched ${new Date(billing.fetchedAt).toLocaleTimeString()}` : ''}</span>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

const s = {
  layout: { display:'flex', background:'#f5f7fa', minHeight:'100vh', fontFamily:"'Inter','Segoe UI',sans-serif" },
  main: { marginLeft:0, flex:1, padding:'12px 12px' },
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  breadcrumb: { color:'#94a3b8', fontSize:12, fontWeight:500, marginBottom:4 },
  pageTitle: { margin:0, color:'#0f172a', fontSize:24, fontWeight:700, letterSpacing:'-0.3px' },
  pageSubtitle: { margin:'4px 0 0', color:'#94a3b8', fontSize:13 },
  refreshTime: { color:'#94a3b8', fontSize:12 },
  outlineBtn: { background:'#fff', border:'1.5px solid #e2e8f0', color:'#374151', borderRadius:8, padding:'8px 16px', cursor:'pointer', fontSize:13, fontWeight:500 },
  primaryBtn: { background:'linear-gradient(135deg,#1d4ed8,#2563eb)', border:'none', color:'#fff', borderRadius:8, padding:'9px 18px', cursor:'pointer', fontSize:13, fontWeight:600 },
  errorBox: { background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', color:'#dc2626', fontSize:14, marginBottom:20 },
  skeletonGrid: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:24 },
  skeleton: { height:110, borderRadius:12, background:'#e2e8f0', animation:'pulse 1.5s ease-in-out infinite' },
  accountBar: { display:'flex', alignItems:'center', gap:0, background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'12px 20px', marginBottom:20, boxShadow:'0 1px 4px rgba(0,0,0,0.04)', flexWrap:'wrap', gap:12 },
  gcpBadge: { display:'flex', alignItems:'center', gap:8, background:'#eff6ff', color:'#1d4ed8', padding:'6px 12px', borderRadius:8, fontSize:12, fontWeight:600, marginRight:8 },
  accountItem: { display:'flex', flexDirection:'column', gap:2, paddingLeft:16, borderLeft:'1px solid #f1f5f9' },
  accountLabel: { color:'#94a3b8', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px' },
  accountValue: { fontWeight:600, fontSize:13, color:'#0f172a' },
  statsGrid: { display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:16, marginBottom:20 },
  statCard: { background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'20px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' },
  statIcon: { width:40, height:40, borderRadius:10, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, marginBottom:14 },
  statValue: { fontSize:28, fontWeight:700, letterSpacing:'-0.5px', marginBottom:4 },
  statLabel: { color:'#64748b', fontSize:13, marginBottom:2 },
  statSub: { color:'#94a3b8', fontSize:11 },
  chartsRow: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 },
  chartCard: { background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, padding:'22px', boxShadow:'0 1px 4px rgba(0,0,0,0.04)' },
  chartHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 },
  chartTitle: { margin:0, color:'#0f172a', fontSize:15, fontWeight:600 },
  badge: { background:'#f1f5f9', color:'#64748b', fontSize:11, padding:'4px 10px', borderRadius:20 },
  empty: { display:'flex', alignItems:'center', justifyContent:'center', height:220, color:'#94a3b8', fontSize:14 },
  tableCard: { background:'#fff', border:'1px solid #e2e8f0', borderRadius:12, overflow:'hidden', marginBottom:16, boxShadow:'0 1px 4px rgba(0,0,0,0.04)' },
  tableHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'18px 20px', borderBottom:'1px solid #f1f5f9' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', color:'#64748b', fontSize:11, fontWeight:600, letterSpacing:'0.5px', textTransform:'uppercase', padding:'10px 20px', borderBottom:'1px solid #f1f5f9' },
  td: { padding:'14px 20px', color:'#374151', fontSize:13, borderBottom:'1px solid #f8fafc', transition:'background 0.1s' },
  code: { background:'#f1f5f9', padding:'2px 8px', borderRadius:5, fontFamily:'monospace', fontSize:12, color:'#475569' },
  linkBtn: { background:'none', border:'none', color:'#2563eb', cursor:'pointer', fontSize:13, fontWeight:500 },
  budgetRow: { display:'flex', alignItems:'center', gap:16, padding:'12px 20px', borderBottom:'1px solid #f8fafc' },
  statusBar: { background:'#fff', border:'1px solid #dcfce7', borderRadius:10, padding:'12px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' },
};